import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RegisterViz } from '@pasqal/core/ui/components/pulser-studio/components/RegisterViz';
import { Slider } from '@pasqal/core/ui/components/pulser-studio/components/Slider';
import AtomLegend from '~/components/AtomLegend';
import IconButton from '~/components/IconButton/IconButton';
import PatternGenerator from '~/components/PatternGenerator/PatternGenerator';
import ZoomControl from '~/components/ZoomControl/ZoomControl';
import { getChannelKind } from '~/models/studio';
import { useContextMenu } from '~/systems/contextMenu';
import {
  getAtomStateFromComponents,
  useCurrentFrame
} from '~/systems/currentFrame';
import {
  useExperiment,
  waveformDuration,
  waveformSampleAt
} from '~/systems/experiment';
import { useUiState } from '~/systems/uiState';
import {
  Bounds,
  findClosestValidAtomsPositions,
  getConnections,
  keyboardConsumed,
  useDOMNodeSize,
  Vec2
} from '~/util';
import { useWindowEvent } from '~/util/hooks';
import type { Atom, Pulse } from '~/models/studio';
import type { MeasuredComponent } from '~/systems/simulation';
import type {
  AtomLegendDetails,
  Connection,
  Orbit,
  PulseDetails,
  PulseEffect
} from '~/util/registerData';

import './Register.css';

interface RegisterProps {
  headerNode: HTMLElement | null;
}

interface DraggedAtom {
  uuid: string;
  updateIndex: number;
  base: Vec2;
}

const MIN_SCALE_FACTOR = 0.25;
const MAX_SCALE_FACTOR = 5;
const SCALE_FACTOR_INC = 0.25;
const ORBIT_RADIUS = [18, 12, 35];
const PROTECTION_AREA = 2;
const MAX_OPACITY = 0.5;

export default function Register({ headerNode }: RegisterProps) {
  const {
    addManyAtoms,
    atoms,
    channels,
    commitedAtoms,
    commitedBasis,
    commitedDevice,
    computedPulses,
    createAtom,
    device,
    getBlockadeRadius,
    getC6Coefficient,
    openTransaction
  } = useExperiment();
  const currentFrame = useCurrentFrame();
  const uiState = useUiState();
  const menu = useContextMenu();

  const viewportRef = useRef<HTMLDivElement>(null);
  const viewportSize = useDOMNodeSize(viewportRef);

  const dragTxRef = useRef<ReturnType<typeof openTransaction> | null>(null);
  const dragAtomsRef = useRef<DraggedAtom[]>([]);
  const dragOtherPositionsRef = useRef<Vec2[]>([]);

  const [scaleFactor, setScaleFactor] = useState(1);
  const [userOpenedGenerator, setUserOpenedGenerator] = useState(false);

  const noAtoms = atoms.length === 0;
  const showGenerator = noAtoms || userOpenedGenerator;
  const atomCapReached = atoms.length >= device.maxAtomNum;

  const connections = useMemo((): Connection[] => {
    const positions = atoms.map((atom) => atom.pos);
    const rydbergRadius = getBlockadeRadius(uiState.previewAmplitude);
    const c6Coefficient = getC6Coefficient();
    return getConnections(positions, rydbergRadius, c6Coefficient);
  }, [atoms, getBlockadeRadius, getC6Coefficient, uiState.previewAmplitude]);

  useWindowEvent('keydown', (event: KeyboardEvent) => {
    if (keyboardConsumed()) {
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      removeSelectedAtoms();
    }
  });

  function removeSelectedAtoms() {
    const atoms = uiState.atomSelection;
    if (atoms.size > 0) {
      const tx = openTransaction('removeSelectedAtoms');
      for (const uid of atoms) {
        tx.push({ kind: 'remove_atom', uuid: uid });
      }
      tx.commit();
    }
  }

  function removeSingleAtom(uid: string) {
    const tx = openTransaction('removeSingleAtom');
    tx.push({ kind: 'remove_atom', uuid: uid });
    tx.commit();
  }

  function cloneSelectedAtoms() {
    const selection = uiState.atomSelection;
    if (selection.size > 0) {
      const conflictRadius = device.minAtomDistance;
      const maxDistance = device.maxRadialDistance;

      const allAtoms = commitedAtoms;
      const atomsToClone = allAtoms.filter((atom) => selection.has(atom.uuid));
      const allPositions = allAtoms.map((atom) => atom.pos);
      const clonePositions = atomsToClone.map((atom) => atom.pos);
      const finalPositions = findClosestValidAtomsPositions(
        Vec2.zero(),
        clonePositions,
        allPositions,
        conflictRadius,
        maxDistance
      );

      const newSelection = addManyAtoms(finalPositions);
      uiState.replaceAtomSelection(newSelection);
    }
  }

  const atomOrbits = useMemo((): Orbit[] => {
    const hoveredState = uiState.hoveredState;
    const numAtoms = currentFrame.numAtoms;
    const states =
      hoveredState == null
        ? currentFrame.averageState
        : getAtomStateFromComponents(
            [hoveredState] as MeasuredComponent[],
            numAtoms,
            commitedBasis
          );

    if (states.length !== atoms.length) {
      return [];
    }
    const orbits: Orbit[] = [];
    for (let idx = 0; idx < atoms.length; idx++) {
      const atom = atoms[idx];
      const state = states[idx];
      const sum = state[0] + state[1] + state[2];
      const scale = sum === 0 ? 1 : 3 / sum;

      for (let s = 0; s < state.length; s++) {
        if (state[s] > 0) {
          orbits.push({
            uuid: atom.uuid,
            x: atom.pos.x,
            y: atom.pos.y,
            radius: ORBIT_RADIUS[s],
            opacity: state[s] * scale
          });
        }
      }
    }
    return orbits;
  }, [
    atoms,
    commitedBasis,
    currentFrame.averageState,
    currentFrame.numAtoms,
    uiState.hoveredState
  ]);

  const getPulseDetails = useCallback(
    (choose: (pulse: Pulse) => boolean): PulseDetails[] => {
      const currentTime = currentFrame.currentTime;
      const details: PulseDetails[] = [];
      for (const computedPulse of computedPulses) {
        const { pulse, layout } = computedPulse;
        if (Bounds.containsValue(layout.time, currentTime) && choose(pulse)) {
          const pulseTime = currentTime - layout.time.min;
          const channel = channels.find((ch) => ch.uuid === pulse.channelUuid);
          if (channel == null) {
            continue;
          }

          let sum = 0;
          const cumulativeDurations = pulse.amplitude.map((wf) => {
            sum += waveformDuration(wf);
            return sum;
          });
          const wfIdx = cumulativeDurations.findIndex((dur) => dur > pulseTime);
          if (wfIdx === -1) {
            continue;
          }

          const wfStartTime = wfIdx > 0 ? cumulativeDurations[wfIdx - 1] : 0;
          const sample = waveformSampleAt(
            pulse.amplitude[wfIdx],
            pulseTime - wfStartTime
          );
          details.push({ pulse, sample, channel });
        }
      }
      return details;
    },
    [channels, computedPulses, currentFrame.currentTime]
  );

  const atomPulseEffect = useMemo((): PulseEffect[] => {
    const pulseEffects: PulseEffect[] = [];

    const pulseDetails = getPulseDetails(() => true);
    for (const { pulse, sample, channel } of pulseDetails) {
      const opacity =
        Math.sqrt(Math.max(sample / channel.maxAmp, 0)) * MAX_OPACITY;
      const targetAtom =
        pulse.targetUuid == null
          ? null
          : atoms.find((a) => a.uuid === pulse.targetUuid) || null;
      let position = Vec2.ZERO;
      let radius = PROTECTION_AREA;
      if (targetAtom != null) {
        position = targetAtom.pos;
      } else if (atoms.length > 0) {
        position = Vec2.ZERO;
        radius = device.maxRadialDistance;
      }
      pulseEffects.push({
        uuid: pulse.uuid,
        x: position.x,
        y: position.y,
        radius,
        opacity,
        channelClass: getChannelKind(channel)
      });
    }
    return pulseEffects;
  }, [atoms, device.maxRadialDistance, getPulseDetails]);

  const collisionRadius = device.minAtomDistance;

  function generatePattern(pattern: Vec2[]) {
    const allAtoms = commitedAtoms;
    const allPositions = allAtoms.map((atom) => atom.pos);
    const dev = commitedDevice;
    const conflictRadius = dev.minAtomDistance;
    const maxDistance = dev.maxRadialDistance;

    const finalPositions = findClosestValidAtomsPositions(
      Vec2.zero(),
      pattern,
      allPositions,
      conflictRadius,
      maxDistance
    );
    const newSelection = addManyAtoms(finalPositions);
    uiState.replaceAtomSelection(newSelection);
    setUserOpenedGenerator(false);
    setScaleFactor(1);
  }

  function getAtomLegend(atomIdx: number | null): AtomLegendDetails {
    const atom = atomIdx == null ? null : atoms[atomIdx];
    const pulseDetails =
      atom == null
        ? null
        : getPulseDetails(
            (pulse) =>
              pulse.targetUuid === atom.uuid || pulse.targetUuid === null
          )[0] || null;
    return { atom, atomIdx, pulseDetails };
  }

  function hoverAtom(index: number | null) {
    uiState.setHoveredAtom(index);
  }

  function toggleShowAtomsInteraction() {
    uiState.setShowAtomsInteractions(!uiState.showAtomsInteractions);
  }

  function registerMenu() {
    menu.registerOnce({ label: 'Add atom', action: createAtom });
    menu.registerOnce({
      label: uiState.showAtomsInteractions
        ? 'Hide Atoms Interactions'
        : 'Show Atoms Interactions',
      action: toggleShowAtomsInteraction
    });
  }

  function onAtomMenu(atom: Atom) {
    menu.registerOnce({
      label: `Delete atom ${atom.label}`,
      action: () => removeSingleAtom(atom.uuid)
    });
    if (uiState.atomSelection.has(atom.uuid)) {
      if (uiState.atomSelection.size > 1) {
        menu.registerOnce({
          label: 'Clone selected atoms',
          action: cloneSelectedAtoms
        });
      }
      menu.registerOnce({
        label: 'Delete selected atoms',
        action: removeSelectedAtoms
      });
    }
  }

  return (
    <>
      {headerNode &&
        !showGenerator &&
        createPortal(
          <ZoomControl
            zoomOutDisabled={scaleFactor <= MIN_SCALE_FACTOR}
            zoomInDisabled={scaleFactor >= MAX_SCALE_FACTOR}
            onZoomOut={() => setScaleFactor((s) => s - SCALE_FACTOR_INC)}
            onZoomIn={() => setScaleFactor((s) => s + SCALE_FACTOR_INC)}
            onFit={() => setScaleFactor(1)}
          >
            <Slider
              value={scaleFactor}
              onChange={setScaleFactor}
              min={MIN_SCALE_FACTOR}
              max={MAX_SCALE_FACTOR}
              logarithmic
              style={{ width: '90px' }}
            />
          </ZoomControl>,
          headerNode
        )}
      <div className="Register">
        <div ref={viewportRef} className="Register-viewportNode">
          {showGenerator ? (
            <>
              <div className="Register-floatingButtons">
                {!noAtoms && (
                  <IconButton
                    label="Close"
                    icon="xmark"
                    disabled={atomCapReached}
                    onClick={() => setUserOpenedGenerator(false)}
                  />
                )}
              </div>
              <PatternGenerator
                maxPoints={device.maxAtomNum}
                minSep={device.minAtomDistance}
                maxDist={device.maxRadialDistance}
                rootSize={viewportSize}
                onGenerate={generatePattern}
              />
            </>
          ) : (
            <>
              <div className="Register-floatingButtons">
                <IconButton
                  label="Add atom"
                  icon="add-atom"
                  background="always"
                  padding={4}
                  disabled={atomCapReached}
                  onClick={() => createAtom()}
                />
                <IconButton
                  label="Add pattern"
                  icon="add-many"
                  background="always"
                  padding={4}
                  disabled={atomCapReached}
                  onClick={() => setUserOpenedGenerator(true)}
                />
              </div>
              <RegisterViz
                atoms={atoms}
                commitedAtoms={commitedAtoms}
                atomOrbits={atomOrbits}
                connections={connections}
                showInteractions={uiState.showAtomsInteractions}
                maxRadialDistance={device.maxRadialDistance}
                pulseEffects={atomPulseEffect}
                collisionRadius={collisionRadius}
                hoveredAtomIdx={uiState.hoveredAtom}
                selectedAtomIds={uiState.atomSelection}
                getAtomLegend={getAtomLegend}
                renderLegend={({ atomLegend, viewport }) => (
                  <AtomLegend
                    atom={atomLegend.atom}
                    atomIdx={atomLegend.atomIdx}
                    pulseDetails={atomLegend.pulseDetails}
                    viewport={viewport}
                  />
                )}
                scaleFactor={scaleFactor}
                onHoverAtom={hoverAtom}
                onSelectAtom={uiState.selectAtom}
                onUnselectAtom={uiState.removeAtomSelection}
                onReplaceAtomSelection={uiState.replaceAtomSelection}
                onUpdateDragging={(scaledMove, dragUids) => {
                  if (dragTxRef.current == null) {
                    dragTxRef.current = openTransaction('dragAtoms');
                    const draggedUids = new Set(
                      dragUids ?? uiState.atomSelection
                    );
                    const dragged: DraggedAtom[] = [];
                    for (const uid of draggedUids) {
                      const atom = commitedAtoms.find((a) => a.uuid === uid);
                      if (atom == null) {
                        continue;
                      }
                      const updateIndex = dragTxRef.current.push({
                        kind: 'update_atom',
                        uuid: uid,
                        props: { pos: atom.pos }
                      });
                      dragged.push({ uuid: uid, updateIndex, base: atom.pos });
                    }
                    dragAtomsRef.current = dragged;
                    dragOtherPositionsRef.current = commitedAtoms
                      .filter((atom) => !draggedUids.has(atom.uuid))
                      .map((atom) => atom.pos);
                  }
                  const dragged = dragAtomsRef.current;
                  const desiredPositions = dragged.map(({ base }) => ({
                    x: base.x + scaledMove.x,
                    y: base.y + scaledMove.y
                  }));
                  const validPositions = findClosestValidAtomsPositions(
                    Vec2.zero(),
                    desiredPositions,
                    dragOtherPositionsRef.current,
                    device.minAtomDistance,
                    device.maxRadialDistance
                  );
                  for (let i = 0; i < dragged.length; i++) {
                    const { uuid, updateIndex } = dragged[i];
                    dragTxRef.current.update(updateIndex, () => ({
                      kind: 'update_atom',
                      uuid,
                      props: { pos: validPositions[i] }
                    }));
                  }
                }}
                onValidateDragging={() => {
                  dragTxRef.current?.commit();
                  dragTxRef.current = null;
                  dragAtomsRef.current = [];
                  dragOtherPositionsRef.current = [];
                }}
                onCancelDragging={() => {
                  dragTxRef.current?.revert();
                  dragTxRef.current = null;
                  dragAtomsRef.current = [];
                  dragOtherPositionsRef.current = [];
                }}
                onAtomContextMenu={onAtomMenu}
                onRegisterContextMenu={registerMenu}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
